import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CategoriesService, ProductService, ProductBankProductService } from '@shabic/core';
import { FormComponent, FORM_CONFIG } from '@shabic/form-control';
import {
  ApiErrorResponse,
  ApiResponse,
  Category,
  countries,
  countryOptions,
  currencies,
  IProductPayload,
  isErrorResponse,
  Option,
  Product,
} from '@shabic/models';
import { BehaviorSubject, Observable, finalize } from 'rxjs';

@Component({
  selector: 'shabic-product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['./product-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: FORM_CONFIG, useValue: { markRequiredFields: true } }],
})
export class ProductFormComponent
  extends FormComponent<unknown>
  implements OnInit
{
  private _pictures = new BehaviorSubject<File[]>([]);
  private _removePictures = new Set<number>();

  @Input() product?: Product;
  @Input() request!: (
    product: IProductPayload,
    removePictures?: number[]
  ) => Observable<ApiResponse<unknown | ApiErrorResponse>>;
  existPictures!: string[];

  readonly formGroup = new FormGroup<any>({
    nameFirst: new FormControl(null),
    nameSecond: new FormControl(null),
    descriptionFirst: new FormControl(null),
    descriptionSecond: new FormControl(null),
    brand: new FormControl(null),
    code: new FormControl(null),
    barCode: new FormControl(null, [Validators.required]),
    govtProductCode: new FormControl(null),
    country: new FormControl(null),
    categoryId: new FormControl(null, [Validators.required]),
    packing: new FormControl(null),
    packingSecond: new FormControl(null),
    priceFirst: new FormControl(null),
    priceSecond: new FormControl(null),
    measurement: new FormControl(null),
    measurementSecond: new FormControl(null),
    packingQuantity: new FormControl(null),
    dimension: new FormControl(null),
    weight: new FormControl(null),
    color: new FormControl(null),
    images: new FormControl(null),
    typeOfPackaging: new FormControl('retail'),
    food: new FormControl('yes'),
    specialHandling: new FormControl('yes'),
    vat: new FormControl(null),
    discount: new FormControl(null),
    grand: new FormControl(null),
    quantity: new FormControl(null),
    currency: new FormControl('USD'),
  });

  get currentCurrency(): string {
    return this.formGroup.get('currency')?.value;
  }

  categories$!: Observable<Option[]>;
  private categories: Option[] = [];
  readonly currencies = currencies;
  readonly countries$ = new BehaviorSubject(
    countryOptions(this.translateService)
  );

  constructor(
    private categoriesService: CategoriesService,
    private translateService: TranslateService,
    private productService: ProductService,
    private productBankProductService: ProductBankProductService
  ) {
    super();

    this.categories$ = this.categoriesService.categories$;
    this.countries$ = new BehaviorSubject(
      countryOptions(this.translateService)
    );

    // Subscribe to categories to keep local reference
    this.categories$.subscribe(categories => {
      this.categories = categories;
    });
  }

  ngOnInit(): void {
    this.categoriesService.updateCategories().subscribe(response => {
      if (this.product) {
        const categories = !isErrorResponse(response) ? response.payload : [];
        const payload = this.product.getProductPayload();

        this.formGroup.patchValue({
          ...payload,
          categoryId:
            typeof payload.categoryId === 'number'
              ? categories
                  .find((el: any) => el.id === payload.categoryId)
                  ?.name.get('en')
              : payload.categoryId,
          food: payload.food ? 'yes' : 'no',
          specialHandling: payload.specialHandling ? 'yes' : 'no',
          currency: payload.currencyFirst,
        });
        this.formGroup.get('barCode')?.disable();
        this.existPictures = this.product.getMedias();
      }
    });
  }

  onSubmit(): void {
    const value = this.formGroup.value;
    const body = {
      ...value,
      food: value.food === 'yes',
      specialHandling: value.specialHandling === 'yes',
      priceSecond: value.priceSecond || 0,
      vat: value.vat || 0,
      categoryId: value.categoryId
        ? this.categoriesService.getOptionValue(value.categoryId)
        : null,
      barCode: this.product ? this.product.barCode : value.barCode,
      currencyFirst: value.currency,
      currencySecond: value.currency,
      mediaId: this._pictures.value.length ? this._pictures.value : null,
    };

    this.submitForm(
      this.formGroup,
      this.request(
        body as IProductPayload,
        Array.from(this._removePictures.values())
      )
    ).subscribe();
  }

  setCurrency(currency: string): void {
    this.formGroup.patchValue({
      currency,
    });
  }

  onPicturesChanged(pictures: File[]) {
    this._pictures.next(pictures);
  }

  onPictureRemove(url: string) {
    const media = this.product?.getMediaByUrl(url);

    if (media) {
      this._removePictures.add(media.id);
      this.existPictures = this.existPictures.filter(el => el !== url);
    }
  }

  searchByBarCode(): void {
    const barCode = this.formGroup.get('barCode')?.value;
    if (!barCode) return;

    // Reset form except barcode field
    const currentBarCode = this.formGroup.get('barCode')?.value;
    this.formGroup.reset({
      barCode: currentBarCode,
      typeOfPackaging: 'retail', // Reset to default values
      food: 'yes',
      specialHandling: 'yes',
      currency: 'USD'
    });
    
    // Reset pictures
    this._pictures.next([]);
    this._removePictures.clear();
    this.existPictures = [];

    this.submitForm(
      this.formGroup,
      this.productBankProductService.getProductByBarCode(barCode),
      { stopLoading: true, returnResponse: true }
    ).subscribe(response => {
      const apiResponse = response as ApiResponse<Product | ApiErrorResponse>;
      if (isErrorResponse(apiResponse)) {
        // Handle error response with error_key from params
        const errorPayload = apiResponse.payload;
        if (errorPayload.status === 400) {
          // Only set field-level error
          const barCodeControl = this.formGroup.get('barCode');
          if (barCodeControl) {
            barCodeControl.setErrors({
              api: this.translateService.instant(errorPayload.params.error_key || 'error.invalid-product-barCode')
            });
            // Clear form-level errors that might have been set by submitForm
            this.formGroup.setErrors(null);
          }
        }
      } else if (apiResponse.payload instanceof Product) {
        const product = apiResponse.payload;
        const payload = product.getProductPayload();
        
        // Get fresh categories and map category name
        this.categoriesService.updateCategories().subscribe(categoriesResponse => {
          const categories = !isErrorResponse(categoriesResponse) ? categoriesResponse.payload : [];
          
          this.formGroup.patchValue({
            ...payload,
            categoryId: typeof payload.categoryId === 'number'
              ? categories.find((el: any) => el.id === payload.categoryId)?.name.get('en')
              : payload.categoryId,
            food: payload.food ? 'yes' : 'no',
            specialHandling: payload.specialHandling ? 'yes' : 'no',
            currency: payload.currencyFirst,
          });
          
          const medias = product.getMedias();
          if (medias?.length) {
            this.existPictures = medias;
          }
        });
      }
    });
  }
}
